import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wand2, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function LiveDemo() {
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const fullPlaceholder = 'A fitness app dashboard with dark mode...';
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setPlaceholderText(fullPlaceholder.slice(0, i));
      i++;
      if (i > fullPlaceholder.length) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue) return;
    setIsGenerating(true);
    setShowResults(false);
    // Simulate generation delay
    setTimeout(() => {
      setIsGenerating(false);
      setShowResults(true);
    }, 2500);
  };
  return <section className="py-24 px-6 bg-[#0a0a0a] relative overflow-hidden" data-id="element-105">
      <div className="max-w-6xl mx-auto" data-id="element-106">
        <div className="text-center mb-12" data-id="element-107">
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-108">
            Try it yourself
          </h2>
          <p className="text-[#a3a3a3]" data-id="element-109">See the magic happen in real-time.</p>
        </div>

        {/* Input Area */}
        <div className="max-w-2xl mx-auto mb-16 relative z-10" data-id="element-110">
          <form onSubmit={handleGenerate} className="relative" data-id="element-111">
            <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b35]/20 to-purple-500/20 blur-xl opacity-50 rounded-full" data-id="element-112" />
            <div className="relative flex items-center bg-[#111] border border-[#333] rounded-full p-2 shadow-2xl focus-within:border-[#ff6b35] transition-colors duration-300" data-id="element-113">
              <Wand2 className="w-5 h-5 text-[#666] ml-4" data-id="element-114" />
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={placeholderText} className="flex-1 bg-transparent border-none focus:ring-0 text-[#f5f5f5] placeholder-[#444] px-4 py-3 outline-none" data-id="element-115" />
              <Button type="submit" disabled={isGenerating || !inputValue} className="rounded-full px-6 py-2 min-w-[120px]" data-id="element-116">
                {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-117" /> : 'Generate'}
              </Button>
            </div>
          </form>
        </div>

        {/* Results Area */}
        <div className="min-h-[400px] relative" data-id="element-118">
          <AnimatePresence mode="wait" data-id="element-119">
            {!showResults && !isGenerating && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center border-2 border-dashed border-[#222] rounded-2xl" data-id="element-120">
                <p className="text-[#444]" data-id="element-121">
                  Enter a prompt above to generate mockups
                </p>
              </motion.div>}

            {isGenerating && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-id="element-122">
                <div className="w-16 h-16 border-4 border-[#222] border-t-[#ff6b35] rounded-full animate-spin" data-id="element-123" />
                <p className="text-[#ff6b35] font-medium animate-pulse" data-id="element-124">
                  Analyzing prompt...
                </p>
              </motion.div>}

            {showResults && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-125">
                {[1, 2, 3, 4].map(i => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.1
            }} className="aspect-video bg-[#111] rounded-xl border border-[#222] overflow-hidden hover:border-[#ff6b35]/50 transition-colors group relative" data-id="element-126">
                    {/* Mock UI Content */}
                    <div className="absolute inset-0 p-6 flex flex-col gap-4 opacity-50 group-hover:opacity-80 transition-opacity" data-id="element-127">
                      <div className="h-4 w-1/3 bg-[#333] rounded" data-id="element-128" />
                      <div className="flex gap-4" data-id="element-129">
                        <div className="h-32 w-full bg-[#222] rounded-lg" data-id="element-130" />
                        <div className="h-32 w-full bg-[#222] rounded-lg" data-id="element-131" />
                      </div>
                      <div className="h-4 w-2/3 bg-[#333] rounded" data-id="element-132" />
                    </div>

                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-133">
                      <div className="bg-[#ff6b35] p-2 rounded-lg text-white" data-id="element-134">
                        <ImageIcon className="w-4 h-4" data-id="element-135" />
                      </div>
                    </div>
                  </motion.div>)}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}